import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getToken, removeToken } from '@shared/services/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that ensures user is authenticated before rendering children.
 * Redirects to /login if no token is found, preserving the intended destination for post-login redirect.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectingLocationKey = useRef<string | null>(null);

  useEffect(() => {
    // StrictMode replays effects in development. Remember the route entry so that
    // clearing an invalid token cannot turn a more specific expiry redirect into
    // a second, generic redirect to /login.
    if (redirectingLocationKey.current === location.key) return;

    const token = getToken();

    if (!token) {
      // No token found, redirect to login with current location for post-login redirect
      redirectingLocationKey.current = location.key;
      navigate('/login', { state: { from: location }, replace: true });
      return;
    }

    // Optional: Check if token is expired
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        // Invalid token format — removeToken also clears the shared fetch cache
        redirectingLocationKey.current = location.key;
        removeToken();
        navigate('/login', { state: { from: location }, replace: true });
        return;
      }

      const payload = JSON.parse(window.atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (payload.exp < currentTime) {
        // Token expired
        redirectingLocationKey.current = location.key;
        removeToken();
        navigate('/login?session_expired=true', { state: { from: location }, replace: true });
        return;
      }
    } catch {
      // Error parsing token, remove it and redirect
      redirectingLocationKey.current = location.key;
      removeToken();
      navigate('/login?session_expired=true', { state: { from: location }, replace: true });
      return;
    }
  }, [navigate, location]);

  // If we have a valid token, render children
  return <>{children}</>;
};

export default ProtectedRoute;
